// Pure helpers for the claim-code detail save flows (unit-tested).
// The control plane is the source of truth for licenses; the dashboard D1
// is the source of truth for claim-code rows shown on this page.

export type ClaimStatus = 'issued' | 'consumed' | 'revoked' | 'expired';

export type LicenseState = 'active' | 'suspended' | 'expired' | 'terminated';

const CLAIM_STATUSES: ClaimStatus[] = ['issued', 'consumed', 'revoked', 'expired'];

/** Normalize a status coming from the UI. Accepts the legacy 'active' alias for 'issued'. */
export function normalizeClaimStatus(input: unknown): ClaimStatus | null {
  if (typeof input !== 'string') return null;
  const v = input.trim().toLowerCase();
  if (v === 'active') return 'issued';
  return (CLAIM_STATUSES as string[]).includes(v) ? (v as ClaimStatus) : null;
}

const LICENSE_STATES: LicenseState[] = ['active', 'suspended', 'expired', 'terminated'];

/** Normalize a license state. Accepts the legacy dashboard 'inactive' alias for 'suspended'. */
export function normalizeLicenseState(input: unknown): LicenseState | null {
  if (typeof input !== 'string') return null;
  const v = input.trim().toLowerCase();
  if (v === 'inactive') return 'suspended';
  return (LICENSE_STATES as string[]).includes(v) ? (v as LicenseState) : null;
}

/** Normalize kill_switch from the 0/1/boolean shapes used across the UI. */
export function normalizeKillSwitch(input: unknown): boolean | null {
  if (typeof input === 'boolean') return input;
  if (input === 1 || input === '1') return true;
  if (input === 0 || input === '0') return false;
  return null;
}

/**
 * Parse a YYYY-MM-DD date input as UTC noon (avoids the midnight timezone
 * shift that moves the day backwards/forwards in some timezones).
 * Returns epoch seconds, or null for empty/invalid input.
 */
export function parseExpiryDateInput(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const ms = Date.parse(`${v}T12:00:00Z`);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 1000);
}

/** Format epoch seconds as a YYYY-MM-DD date input value (UTC). */
export function formatExpiryDateInput(expiresAt: unknown): string {
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt) || expiresAt <= 0) return '';
  return new Date(expiresAt * 1000).toISOString().split('T')[0];
}

export interface LicenseUpsertBody {
  tier: string;
  seat_limit: number | null;
  token_cap_monthly: number | null;
  starts_at: number;
  expires_at: number;
  grace_period_days: number;
  state: LicenseState;
  kill_switch: boolean;
  actor: string;
  reason?: string;
}

/**
 * Build the full control-plane license upsert body from the loaded license
 * merged with the user's edits. The control plane requires every field, so
 * unchanged values are carried over from the current license.
 * Returns null when required data is missing/invalid.
 */
export function toLicenseUpsertBody(
  current: Record<string, any> | null | undefined,
  edits: Record<string, any> | null | undefined,
  actor: unknown
): LicenseUpsertBody | null {
  const merged = { ...(current ?? {}), ...(edits ?? {}) };
  const state = normalizeLicenseState(merged.state);
  const killSwitch = normalizeKillSwitch(merged.kill_switch);
  const expiresAt = typeof merged.expires_at === 'number' ? Math.floor(merged.expires_at) : NaN;
  const startsAt = typeof merged.starts_at === 'number' ? Math.floor(merged.starts_at) : NaN;
  const grace = typeof merged.grace_period_days === 'number' ? Math.floor(merged.grace_period_days) : NaN;
  const actorEmail = typeof actor === 'string' ? actor.trim() : '';

  if (!merged.tier || typeof merged.tier !== 'string') return null;
  if (state === null || killSwitch === null) return null;
  if (!Number.isFinite(startsAt) || startsAt <= 0) return null;
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return null;
  if (!Number.isFinite(grace) || grace < 0 || grace > 365) return null;
  if (!actorEmail || !actorEmail.includes('@')) return null;

  const seatLimit =
    merged.seat_limit === null || merged.seat_limit === undefined ? null : Math.floor(merged.seat_limit);
  const tokenCap =
    merged.token_cap_monthly === null || merged.token_cap_monthly === undefined
      ? null
      : Math.floor(merged.token_cap_monthly);
  if (seatLimit !== null && (!Number.isFinite(seatLimit) || seatLimit <= 0)) return null;
  if (tokenCap !== null && (!Number.isFinite(tokenCap) || tokenCap <= 0)) return null;

  const body: LicenseUpsertBody = {
    tier: merged.tier,
    seat_limit: seatLimit,
    token_cap_monthly: tokenCap,
    starts_at: startsAt,
    expires_at: expiresAt,
    grace_period_days: grace,
    state,
    kill_switch: killSwitch,
    actor: actorEmail,
  };
  if (typeof merged.reason === 'string' && merged.reason.trim()) {
    body.reason = merged.reason.trim().slice(0, 500);
  }
  return body;
}
